<script lang="ts" setup>
import { onMounted, ref } from 'vue'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import ScrollableTable from '@/components/common/ScrollableTable.vue'
import { useFarmClubDealsStore } from '@/store/public/farm-club-deals'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate, formatValue } from '@/utils/format'

const farmClubDeals = useFarmClubDealsStore()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

async function fetchFarmClubDeals(): Promise<void> {
  isLoading.value = true

  try {
    await farmClubDeals.fetch()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching farm club deals: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
}

onMounted(fetchFarmClubDeals)
</script>

<template>
  <main>
    <h1>{{ $t('farm-club-deals.heading') }}</h1>

    <h1 v-if="isLoading" class="busy-indicator-container">
      <BusyIndicator />
    </h1>

    <NotificationBanner
      v-else-if="errorMessage"
      :message="errorMessage"
      is-danger
    />

    <NotificationBanner
      v-else-if="farmClubDeals.deals.length === 0"
      :message="$t('farm-club-deals.no-deals')"
    />

    <ScrollableTable v-else>
      <thead>
        <tr>
          <th>{{ $t('farm-club-deals.bowler') }}</th>
          <th>{{ $t('farm-club-deals.own-club') }}</th>
          <th>{{ $t('farm-club-deals.farm-club') }}</th>
          <th class="fixed-3x">{{ $t('farm-club-deals.valid-from') }}</th>
          <th class="fixed-3x">{{ $t('farm-club-deals.valid-thru') }}</th>
          <th>{{ $t('farm-club-deals.deal-affects') }}</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="deal in farmClubDeals.deals" :key="deal.id">
          <th>{{ formatValue(deal.bowler_name) }}</th>
          <td>
            {{ formatValue(deal.club_name) }}
            ({{ formatValue(deal.club_abbreviation) }})
          </td>
          <td>
            {{ formatValue(deal.farm_club_name) }}
            ({{ formatValue(deal.farm_club_abbreviation) }})
          </td>
          <td class="fixed-3x">
            {{ formatDate(new Date(deal.valid_start_date), false, true) }}
          </td>
          <td class="fixed-3x">
            {{ formatDate(new Date(deal.valid_end_date), false, true) }}
          </td>
          <td>{{ formatValue(deal.deal_affects) }}</td>
        </tr>
      </tbody>
    </ScrollableTable>
  </main>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}
</style>
